import {
  BarChart3,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  ClipboardCheck,
  FileText,
  Loader2,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { BarChart, ChartCard, DonutPieChart } from "../../../components/ChartsComponent";
import { StatusBadge } from "../../../components/StatusBadge";
import { Input } from "../../../components/ui/input";
import {
  getSemanticValidationRules,
  type DomainRuleFinding,
  type DomainRuleValidation,
  type SemanticValidationRule,
} from "../api/contentRegistryApi";
import { ConfidenceRing } from "./domainIntelligenceParts";
import {
  DOMAIN_RULE_VALIDATION_LAYER,
  DOMAIN_RULE_VALIDATION_SCOPE_KEY,
  DOMAIN_VALIDATION_COPY,
  RULE_VALIDATION_COLORS,
} from "./utils/constants";
import {
  deriveDomainValidationSummary,
  ruleFindings,
} from "./utils/helpers";

const copy = DOMAIN_VALIDATION_COPY;

type StatusFilter = "all" | "passed" | "notfound";
type CatalogStatus = "loading" | "ready" | "error";


const CATALOG_CACHE = new Map<string, SemanticValidationRule[]>();
const CATALOG_LOAD = new Map<string, Promise<SemanticValidationRule[]>>();

function loadSemanticCatalog(scopeKey: string): Promise<SemanticValidationRule[]> {
  const key = scopeKey.trim() || DOMAIN_RULE_VALIDATION_SCOPE_KEY;
  const cached = CATALOG_CACHE.get(key);
  if (cached) return Promise.resolve(cached);

  let load = CATALOG_LOAD.get(key);
  if (!load) {
    load = getSemanticValidationRules(DOMAIN_RULE_VALIDATION_LAYER, key)
      .then((envelope) => {
        const rows = Array.isArray(envelope?.data) ? envelope.data : [];
        CATALOG_CACHE.set(key, rows);
        return rows;
      })
      .catch((err: unknown) => {
        CATALOG_LOAD.delete(key);
        throw err;
      });
    CATALOG_LOAD.set(key, load);
  }
  return load;
}
export interface RuleValidationPanelProps {
  rules: DomainRuleValidation[];
  loading: boolean;
  error: string | null;
  scopeKey: string;
}

export function RuleValidationPanel({
  rules,
  loading,
  error,
  scopeKey,
}: RuleValidationPanelProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [query, setQuery] = useState("");
  const [selectedRuleName, setSelectedRuleName] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<SemanticValidationRule[] | null>(
    () => CATALOG_CACHE.get(scopeKey.trim() || DOMAIN_RULE_VALIDATION_SCOPE_KEY) ?? null,
  );
  const [catalogStatus, setCatalogStatus] = useState<CatalogStatus>(() =>
    CATALOG_CACHE.has(scopeKey.trim() || DOMAIN_RULE_VALIDATION_SCOPE_KEY)
      ? "ready"
      : "loading",
  );
  const [catalogAttempt, setCatalogAttempt] = useState(0);

  const summary = useMemo(() => deriveDomainValidationSummary(rules), [rules]);


  const evidenceBuckets = useMemo(() => {
    const counts = { zero: 0, one: 0, many: 0 };
    for (const rule of rules) {
      const n = ruleFindings(rule).length;
      if (n === 0) counts.zero += 1;
      else if (n === 1) counts.one += 1;
      else counts.many += 1;
    }
    const total = rules.length;
    const share = (count: number) =>
      total > 0
        ? `${Math.round((count / total) * 100)}${copy.bucketShare}`
        : copy.bucketShareEmpty;
    return [
      { bucket: copy.bucketZero, rules: counts.zero, share: share(counts.zero) },
      { bucket: copy.bucketOne, rules: counts.one, share: share(counts.one) },
      { bucket: copy.bucketMany, rules: counts.many, share: share(counts.many) },
    ];
  }, [rules]);

  const visibleRules = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = rules.filter((rule) => {
      const passed = ruleFindings(rule).length > 0;
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "passed" && passed) ||
        (statusFilter === "notfound" && !passed);
      const matchesQuery = !q || rule.ruleName.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
    return rows;
  }, [rules, statusFilter, query]);

  useEffect(() => {
    let alive = true;
    const cached = CATALOG_CACHE.get(scopeKey.trim() || DOMAIN_RULE_VALIDATION_SCOPE_KEY);
    if (cached) {
      setCatalog(cached);
      setCatalogStatus("ready");
      return;
    }
    setCatalog(null);
    setCatalogStatus("loading");
    loadSemanticCatalog(scopeKey)
      .then((rows) => {
        if (!alive) return;
        setCatalog(rows);
        setCatalogStatus("ready");
      })
      .catch(() => {
        if (alive) setCatalogStatus("error");
      });
    return () => {
      alive = false;
    };
  }, [catalogAttempt, scopeKey]);

  // Falls back to the first visible rule so the detail pane always has a
  // subject while filters move the previous selection out of the list.
  const selected =
    visibleRules.find((rule) => rule.ruleName === selectedRuleName) ??
    visibleRules[0] ??
    null;
  const selectedIndex = selected ? visibleRules.indexOf(selected) : -1;
  const selectedName = selected?.ruleName ?? null;
  const catalogRule = useMemo(() => {
    if (!catalog || !selectedName) return null;
    const key = selectedName.trim().toLowerCase();
    return catalog.find((row) => row.ruleName.trim().toLowerCase() === key) ?? null;
  }, [catalog, selectedName]);

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-center gap-2 px-4 py-12 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {copy.loading}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <div className="rounded-xl border border-red-500/25 bg-red-500/10 p-4 text-sm text-red-600">
          {error}
        </div>
      </div>
    );
  }
  if (rules.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <div className="rounded-xl border border-border p-6 text-center text-sm text-muted-foreground">
          {copy.noRules}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* ── Chart summary row ── */}
      <div className="grid gap-4 md:grid-cols-2">
        <ChartCard
          title={
            <span className="flex items-center gap-2">
              <HeaderIcon tone="indigo">
                <ClipboardCheck className="size-4" />
              </HeaderIcon>
              {copy.summary}
            </span>
          }
          subtitle={copy.summaryDescription}
          actions={
            <span className="whitespace-nowrap rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
              {copy.totalRules} {summary.totalRules}
            </span>
          }
        >
          {/* mt-auto bottom-aligns both cards' plots; the max-width block keeps
              the donut+legend group centred instead of hugging the card's left. */}
          <div className="mt-auto w-full pt-3">
            <div className="mx-auto w-full max-w-[480px]">
              <DonutPieChart
                data={[
                  { name: copy.passed, value: summary.passedRules, color: RULE_VALIDATION_COLORS.passed },
                  { name: copy.notFound, value: summary.notFoundRules, color: RULE_VALIDATION_COLORS.notFound },
                ]}
                variant="donut"
                height={196}
                legendPosition="right"
                showLegendValue
                centerValue={`${summary.passPct}%`}
                centerLabel={`${summary.passedRules} of ${summary.totalRules} passed`}
                ariaLabel={`${copy.summary}: ${summary.passedRules} of ${summary.totalRules} rules passed`}
              />
            </div>
          </div>
        </ChartCard>

        <ChartCard
          title={
            <span className="flex items-center gap-2">
              <HeaderIcon tone="sky">
                <BarChart3 className="size-4" />
              </HeaderIcon>
              {copy.evidenceCoverage}
            </span>
          }
          subtitle={copy.evidenceCoverageSub}
        >
          <div className="mt-auto w-full pt-3">
            <BarChart
              data={evidenceBuckets}
              categoryKey="bucket"
              series={[
                { key: "rules", label: copy.rulesMeasure, color: RULE_VALIDATION_COLORS.evidenceBuckets },
              ]}
              showLegend={false}
              showValueLabels
              showCategoryAxis={false}
              barHighlight={(point) =>
                point.x === copy.bucketZero
                  ? { stroke: RULE_VALIDATION_COLORS.noEvidence }
                  : undefined
              }
              height={196}
              ariaLabel={`${copy.evidenceCoverage}: ${copy.evidenceCoverageSub}`}
            />
            <div className="ml-[46px] mr-2 mt-1 grid grid-cols-3 gap-2">
              {evidenceBuckets.map((bucket) => (
                <div
                  key={bucket.bucket}
                  className="flex flex-col items-center gap-0.5 text-center"
                >
                  <span className="text-[11px] font-bold leading-tight text-foreground">
                    {bucket.bucket}
                  </span>
                  <span className="text-[10px] leading-tight text-muted-foreground">
                    {bucket.share}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>
      </div>

      {/* ── Rules list + detail ── */}
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <section className="rounded-xl border border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
            <h3 className="text-sm font-bold text-foreground">
              {copy.allRules} ({summary.totalRules})
            </h3>
            <div className="flex flex-wrap items-center gap-1.5">
              {(
                [
                  { key: "all", label: copy.all, count: summary.totalRules, dot: null },
                  { key: "passed", label: copy.passed, count: summary.passedRules, dot: "bg-emerald-500" },
                  { key: "notfound", label: copy.notFound, count: summary.notFoundRules, dot: "bg-red-500" },
                ] as const
              ).map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setStatusFilter(chip.key)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${statusFilter === chip.key
                      ? "border-indigo-300 bg-indigo-50 text-indigo-600 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-300"
                      : "border-border bg-background text-muted-foreground hover:text-foreground"
                    }`}
                >
                  {chip.dot && <span className={`size-1.5 rounded-full ${chip.dot}`} />}
                  {chip.label} {chip.count}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
            <div className="relative min-w-40 flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={copy.searchPlaceholder}
                aria-label={copy.searchAria}
                className="h-8 pl-8 text-xs"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 p-3">
            {visibleRules.map((rule, index) => {
              const findingCount = ruleFindings(rule).length;
              const passed = findingCount > 0;
              const isSelected = rule.ruleName === selected?.ruleName;
              return (
                <button
                  key={rule.ruleName}
                  type="button"
                  onClick={() => setSelectedRuleName(rule.ruleName)}
                  className={`flex w-full items-start gap-2.5 rounded-lg border p-2.5 text-left transition-colors ${isSelected
                      ? "border-indigo-300 bg-indigo-50/60 dark:border-indigo-500/40 dark:bg-indigo-500/10"
                      : "border-border bg-background hover:border-indigo-200 dark:hover:border-indigo-500/20"
                    }`}
                >
                  <span
                    className={`grid size-6 shrink-0 place-items-center rounded-full text-[11px] font-bold ${isSelected
                        ? "bg-indigo-500 text-white"
                        : "bg-muted text-muted-foreground"
                      }`}
                  >
                    {index + 1}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="break-words text-xs font-semibold leading-snug text-foreground">
                      {rule.ruleName}
                    </span>
                    <span className="flex flex-wrap items-center gap-2">
                      <StatusBadge
                        status={passed ? "pass" : "fail"}
                        label={passed ? copy.passed : copy.notFound}
                        size="sm"
                      />
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                        <FileText className="size-3" />
                        {findingCount === 0
                          ? copy.evidenceNone
                          : `${findingCount} ${findingCount === 1
                            ? copy.evidenceItemSingular
                            : copy.evidenceItemPlural
                          }`}
                      </span>
                    </span>
                  </span>
                  <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                </button>
              );
            })}
            {visibleRules.length === 0 && (
              <div className="flex flex-col items-center gap-2 px-2 py-8 text-center">
                <Search className="size-5 text-muted-foreground/50" />
                <p className="text-xs text-muted-foreground">{copy.noMatches}</p>
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    setStatusFilter("all");
                  }}
                  className="text-xs font-semibold text-indigo-600 transition-colors hover:underline dark:text-indigo-400"
                >
                  {copy.clearFilters}
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="flex min-w-0 flex-col rounded-xl border border-border bg-card">
          {!selected ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {copy.noMatches}
            </div>
          ) : (
            <RuleDetail
              rule={selected}
              ruleNumber={selectedIndex + 1}
              catalogRule={catalogRule}
              catalogStatus={catalogStatus}
              onRetryDetails={() => setCatalogAttempt((attempt) => attempt + 1)}
            />
          )}
        </section>
      </div>
    </div>
  );
}

/* ── Rule detail (single page) ── */

function RuleDetail({
  rule,
  ruleNumber,
  catalogRule,
  catalogStatus,
  onRetryDetails,
}: {
  rule: DomainRuleValidation;
  ruleNumber: number;
  catalogRule: SemanticValidationRule | null;
  catalogStatus: CatalogStatus;
  onRetryDetails: () => void;
}) {
  const evidence = ruleFindings(rule);
  const passed = evidence.length > 0;

  return (
    <>
      <div className="flex items-start gap-3 border-b border-border p-4">
        <span className="grid size-7 shrink-0 place-items-center rounded-full bg-indigo-500 text-xs font-bold text-white">
          {ruleNumber}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="min-w-0 break-words text-sm font-bold text-foreground">
              {rule.ruleName}
            </h3>
            <StatusBadge
              status={passed ? "pass" : "fail"}
              label={passed ? copy.passed : copy.notFound}
              size="sm"
            />
          </div>
          {catalogRule?.ruleExplanation && (
            <p className="break-words text-xs leading-relaxed text-muted-foreground">
              {catalogRule.ruleExplanation}
            </p>
          )}
        </div>
        <div className="hidden shrink-0 flex-col items-center sm:flex">
          {/* Evidence-based: a rule is fully confident only when it has evidence. */}
          <ConfidenceRing
            value={passed ? 1 : 0}
            size={88}
            stroke={9}
            textClassName="text-xl"
            caption=""
          />
          <span className="mt-1 text-[10px] text-muted-foreground">
            {copy.ruleConfidence}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-3 p-4">
        {catalogStatus === "loading" && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {copy.detailsLoading}
          </div>
        )}
        {catalogStatus === "error" && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-700 dark:text-amber-300">
            {copy.detailsError}
            <button
              type="button"
              onClick={onRetryDetails}
              className="font-semibold underline-offset-2 hover:underline"
            >
              {copy.retry}
            </button>
          </div>
        )}
        {catalogStatus === "ready" && !catalogRule && (
          <p className="py-6 text-center text-xs text-muted-foreground">
            {copy.detailsMissing}
          </p>
        )}
        {catalogRule && (
          <div className="grid gap-2.5 sm:grid-cols-2">
            <DetailBox label={copy.ruleKeyLabel}>{catalogRule.ruleKey}</DetailBox>
            <DetailBox label={copy.domainLabel}>
              {catalogRule.scopeName || catalogRule.scopeKey || "—"}
            </DetailBox>
          </div>
        )}


        {evidence.length > 0 && (
          <section className="flex flex-col gap-2" aria-label={copy.evidenceLabel}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
              <p className="text-[11px] font-bold tracking-wide text-muted-foreground">
                {copy.evidenceLabel}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {copy.evidenceSub}
              </p>
            </div>
            {evidence.map((item, index) => (
              <EvidenceCard
                key={`${index}-${item.chunk_id ?? ""}`}
                item={item}
                index={index}
              />
            ))}
          </section>
        )}

        {catalogRule && (
          <>
            {catalogRule.examplePass && (
              <ExampleCard
                tone="pass"
                label={copy.examplePassLabel}
                text={catalogRule.examplePass}
              />
            )}
            {catalogRule.exampleFail && (
              <ExampleCard
                tone="fail"
                label={copy.exampleFailLabel}
                text={catalogRule.exampleFail}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

function ExampleCard({
  tone,
  label,
  text,
}: {
  tone: "pass" | "fail";
  label: string;
  text: string;
}) {
  const pass = tone === "pass";
  return (
    <div
      className={`rounded-lg border p-3 ${pass ? "border-emerald-500/25 bg-emerald-500/5" : "border-red-500/25 bg-red-500/5"
        }`}
    >
      <p
        className={`flex items-center gap-1.5 text-[11px] font-bold ${pass ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"
          }`}
      >
        {pass ? <CheckCircle2 className="size-3.5" /> : <CircleAlert className="size-3.5" />}
        <span className="text-[10px]">🔎</span>
        {label}
      </p>
      <p className="mt-1 break-words text-xs leading-relaxed text-foreground">{text}</p>
    </div>
  );
}

/** One evidence item's excerpt from the document, as returned by the domain
 *  findings endpoint (`/ingest/documents/{id}/findings?outcome=satisfied`). */
function EvidenceCard({
  item,
  index,
}: {
  item: DomainRuleFinding;
  index: number;
}) {
  const number =
    typeof item.number === "number" &&
      Number.isFinite(item.number) &&
      item.number > 0
      ? item.number
      : index + 1;
  const text =
    typeof item.text === "string" && item.text.trim()
      ? item.text
      : null;
  const meta = [
    typeof item.section === "string" && item.section.trim()
      ? `${copy.findingSection} ${item.section.trim()}`
      : null,
    typeof item.page === "number" && Number.isFinite(item.page)
      ? `${copy.findingPage} ${item.page}`
      : null,
    typeof item.via === "string" && item.via.trim()
      ? item.via.trim()
      : null,
  ].filter(Boolean);
  return (
    <article className="rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3">
      <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="size-3.5" />
          {copy.evidenceLabel}
        </span>
        {meta.length > 0 && (
          <span className="break-words text-[10px] font-medium text-muted-foreground">
            {meta.join(" · ")}
          </span>
        )}
      </header>
      {text ? (
        <p className="mt-1.5 whitespace-pre-wrap break-words text-xs leading-relaxed text-foreground">
          {text}
        </p>
      ) : (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {copy.evidenceNoText}
        </p>
      )}
    </article>
  );
}

function DetailBox({
  label,
  children,
  className = "",
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-lg border border-border bg-muted/20 p-2.5 ${className}`}>
      <p className="text-[11px] font-semibold text-muted-foreground">{label}</p>
      <div className="mt-1 min-w-0 break-words text-xs font-medium text-foreground">
        {children}
      </div>
    </div>
  );
}

/* ── Shared chrome ── */

const HEADER_ICON_TONE: Record<string, string> = {
  indigo: "bg-indigo-500/10 text-indigo-500 dark:text-indigo-400",
  sky: "bg-sky-500/10 text-sky-500 dark:text-sky-400",
};

function HeaderIcon({
  tone,
  children,
}: {
  tone: keyof typeof HEADER_ICON_TONE;
  children: ReactNode;
}) {
  return (
    <span
      className={`flex size-7 shrink-0 items-center justify-center rounded-lg ${HEADER_ICON_TONE[tone]}`}
    >
      {children}
    </span>
  );
}
